import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@/components/theme-provider'
import { PostHogProvider } from '../components/PostHogProvider'
import { initializeImpersonationBanner } from '@/lib/impersonation-banner'
import i18n from '@/lib/i18n'
import '@/lib/zod-i18n' // Initialize Zod i18n error map
import './application.css'

// TanStack Query client with default caching configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000,   // Cache garbage collected after 10 minutes
      retry: 1,                  // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    },
  },
})

// Define the shape of page props
interface PageProps {
  auth?: {
    user?: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      locale?: string | null
    } | null
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
  posthog?: {
    apiKey: string
    apiHost: string
    enabled: boolean
  }
  [key: string]: unknown
}

// Initialize Inertia app
createInertiaApp({
  // Resolve page components
  resolve: (name) => {
    const pages = import.meta.glob<{ default: React.ComponentType<PageProps> }>(
      '../pages/**/*.tsx',
      { eager: true }
    )
    const page = pages[`../pages/${name}.tsx`]

    if (!page) {
      throw new Error(`Page not found: ${name}`)
    }

    return page
  },

  // Setup React application
  setup({ el, App, props }) {
    const root = createRoot(el)
    const pageProps = props.initialPage.props as PageProps

    // Sync i18n to user's saved locale on initial load
    const userLocale = pageProps.auth?.user?.locale
    if (userLocale && i18n.language !== userLocale) {
      i18n.changeLanguage(userLocale)
    }

    // Sync i18n on subsequent Inertia navigations (e.g., after login)
    router.on('navigate', (event) => {
      const page = event.detail.page
      const navigatedUserLocale = (page.props as PageProps).auth?.user?.locale
      if (navigatedUserLocale && i18n.language !== navigatedUserLocale) {
        i18n.changeLanguage(navigatedUserLocale)
      }
    })

    root.render(
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="system" storageKey="cayuUiTheme">
            <PostHogProvider
              config={pageProps.posthog}
              user={pageProps.auth?.user ? { id: pageProps.auth.user.id, email: pageProps.auth.user.email, name: pageProps.auth.user.name, firstName: '', lastName: '' } : null}
            >
              <App {...props} />
            </PostHogProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </I18nextProvider>
    )
  },

  // Progress bar configuration
  progress: {
    color: '#3b82f6', // Tailwind blue-500
    showSpinner: true,
  },
})

// Initialize impersonation banner functionality
initializeImpersonationBanner()
